/**
 * "게재 준비" 배지 — 업로드 이후 서버 쪽 처리 상태를 사용자 문구로 바꾸는 단일 정의처.
 * Basic(간편 모드)·MoFile 카드·FileDetailModal·Dashboard 승인 대기·PublicUpload 가 모두 이 함수를 쓴다.
 *
 * 원칙: 수십 초 걸리는 작업은 (1) 지금 무슨 단계인지 (2) 움직이는지 (3) 끝났는지/실패했는지를 보인다.
 * 기술 용어("변환", "트랜스코딩", "AV1")는 쓰지 않는다.
 *
 * 입력은 두 출처다.
 *  - server: 목록/overview 응답의 DB 상태(새로고침 후 유일한 근거).
 *  - entry : WS 푸시로 갱신되는 스토어 항목(업로더 탭에서만 온다, 더 신선하다).
 * 스토어가 newest-wins 로 두 출처를 이미 합치므로, entry 가 있으면 entry 를 우선한다.
 *
 * 단계:
 *  - pending / stage=queued → "차례 기다리는 중"(% 무시 — 큐 대기 중 숫자는 의미가 없다)
 *  - processing + stage=prepare(사진→영상) → "1/2 N%"
 *  - processing + stage=encode → 사진 출신이면 "2/2 N%", 영상이면 "N%"
 *  - 업로더 탭에서 WS 가 살아 있는데 45초 넘게 아무 이벤트가 없으면 "시간이 조금 더 걸리고 있어요"
 *    (서버가 재시도 대기 중이거나 원격 인코더가 소스를 받는 중). seed 만 있는 비업로더 탭에는 적용 안 함.
 */

export type BadgeStage = 'prepare' | 'queued' | 'encode';
export type BadgeStatus = 'pending' | 'processing' | 'done' | 'failed';

export interface BadgeServer {
  status?: string | null;
  progress?: number | null;
  /** processing 이면서 encoding_started_at IS NULL — 사진 준비 단계 마커(서버가 계산해 준다). */
  preparing?: boolean | null;
  error?: string | null;
}

export interface BadgeEntry {
  status: BadgeStatus;
  progress: number;
  stage?: BadgeStage;
  /** 이 파일이 prepare 단계를 거쳤는가(사진 출신). 2/2 라벨 근거. */
  hadPrepare?: boolean;
  /** 마지막 WS 이벤트 시각. 지연 판정용. */
  lastEventAt?: number;
  /** 이 항목이 WS 이벤트를 한 번이라도 받았는가(업로더 탭). */
  sawWs?: boolean;
  error?: string;
}

export interface BadgeInput {
  server?: BadgeServer | null;
  entry?: BadgeEntry | null;
  /** WS 소켓이 지금 연결돼 있는가. 끊겨 있으면 지연 판정을 하지 않는다(구 탭 오탐 방지). */
  wsConnected?: boolean;
  now?: number;
}

export type BadgeKind = 'none' | 'queued' | 'prepare' | 'encode' | 'late' | 'failed';

export interface Badge {
  kind: BadgeKind;
  text: string;
  percent: number | null;
  error?: string;
}

export const BADGE_TEXT = {
  queued: '게재 준비 중 · 차례 기다리는 중',
  preparing: '게재 준비 중',
  late: '게재 준비 중 · 시간이 조금 더 걸리고 있어요',
  failed: '게재 준비 실패',
  variantsPending: '화면 맞춤본 준비 중',
  variantsFailed: '일부 화면 맞춤본 실패',
} as const;

export const LATE_AFTER_MS = 45_000;

const pct = (n: number | null | undefined): number | null => {
  if (n === null || n === undefined || !Number.isFinite(n)) return null;
  return Math.max(0, Math.min(100, Math.round(n)));
};

export function transcodeBadge(input: BadgeInput): Badge {
  const { server, entry, wsConnected = false } = input;
  const now = input.now ?? Date.now();

  // 상태 결정 — entry(WS) 우선, 없으면 server.
  const status: string | null = entry?.status ?? server?.status ?? null;
  if (!status || status === 'done') return { kind: 'none', text: '', percent: null };
  if (status === 'failed') {
    return { kind: 'failed', text: BADGE_TEXT.failed, percent: null, error: entry?.error ?? server?.error ?? undefined };
  }

  const stage: BadgeStage = entry?.stage
    ?? (status === 'pending' ? 'queued' : (server?.preparing ? 'prepare' : 'encode'));
  if (status === 'pending' || stage === 'queued') {
    return { kind: 'queued', text: BADGE_TEXT.queued, percent: null };
  }

  // 지연: 업로더 탭(WS 를 받아 본 항목)이고 소켓이 살아 있는데 45초 무이벤트.
  if (wsConnected && entry?.sawWs && entry.lastEventAt && now - entry.lastEventAt > LATE_AFTER_MS) {
    return { kind: 'late', text: BADGE_TEXT.late, percent: null };
  }

  const percent = pct(entry ? entry.progress : server?.progress);
  const hadPrepare = !!entry?.hadPrepare || !!server?.preparing;
  if (stage === 'prepare') {
    const text = percent && percent > 0 ? `게재 준비 중 1/2 ${percent}%` : '게재 준비 중 1/2';
    return { kind: 'prepare', text, percent };
  }
  const frac = hadPrepare ? '2/2 ' : '';
  const text = percent && percent > 0 ? `게재 준비 중 ${frac}${percent}%` : `게재 준비 중${frac ? ` ${frac.trim()}` : ''}`;
  return { kind: 'encode', text, percent };
}

/** 같은 파일의 맞춤본(변형) 항목들을 보고 보조 배지를 고른다. 메인 배지에는 영향 없음. */
export function variantsBadge(variantStatuses: BadgeStatus[]): string | null {
  if (variantStatuses.some(s => s === 'pending' || s === 'processing')) return BADGE_TEXT.variantsPending;
  if (variantStatuses.some(s => s === 'failed')) return BADGE_TEXT.variantsFailed;
  return null;
}
